/**
 * BenchmarkSparkline — tiny 80×24 SVG line chart for the public
 * benchmarks page. Renders the last N values for a single datapoint
 * key, fetched from `/api/public/benchmarks/history`. Defensive: if
 * fewer than 2 points exist, renders nothing (so we never lie about
 * "trend" with a single dot).
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function BenchmarkSparkline({ keyId, width = 80, height = 22 }) {
  const [points, setPoints] = useState([]);

  useEffect(() => {
    if (!keyId) return;
    axios
      .get(`${API}/public/benchmarks/history`, { params: { key: keyId, limit: 12 } })
      .then((r) => setPoints((r.data?.points || []).filter((p) => typeof p.value === "number")))
      .catch(() => setPoints([]));
  }, [keyId]);

  if (points.length < 2) return null;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = width / (values.length - 1);
  const path = values
    .map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const lastV = values[values.length - 1];
  const firstV = values[0];
  const direction = lastV > firstV ? "up" : lastV < firstV ? "down" : "flat";
  const stroke = direction === "up" ? "#dc2626" : direction === "down" ? "#059669" : "#7737F6";

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`${points.length}-point trend: ${direction}`}
      data-testid={`sparkline-${keyId}`}
    >
      <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={(values.length - 1) * step}
        cy={height - ((lastV - min) / range) * (height - 4) - 2}
        r="1.8"
        fill={stroke}
      />
    </svg>
  );
}
